import { useHudStore } from "../../store/hudStore";
import { fill } from "../ui";

// Games module for the Entertainment domain page.
//
// System/xp-rules.md is explicit that the backlog is "a fun tracker, no
// accountability nudges", so nothing here scolds. No days-since, no "still
// unplayed after N months", no completion percentage presented as a shortfall.
// The People module tracks warmth because letting a friendship go cold is a
// real cost; letting a game sit is not. The one number shown against a target
// is the Backlog slayer badge, which is a thing to earn rather than a standard
// being fallen short of.

const TONE: Record<string, { label: string; color: string }> = {
  playing: { label: "Playing", color: "var(--accent-ink)" },
  backlog: { label: "Backlog", color: "var(--mut)" },
  beaten: { label: "Beaten", color: "var(--green-ink)" },
  dropped: { label: "Set down", color: "var(--mut)" },
};

const BADGE_AT = 5;

export default function GamesModule() {
  const games = useHudStore((s) => s.games);
  const playing = games.filter((g) => g.status === "playing");
  const beaten = games.filter((g) => g.status === "beaten").length;
  const queued = games.filter((g) => g.status === "backlog").length;

  const headline = playing.length
    ? "Playing " + playing.map((g) => g.name).join(", ")
    : queued
      ? queued + (queued === 1 ? " game waiting" : " games waiting")
      : "Nothing on the go.";
  const sub = beaten >= BADGE_AT
    ? beaten + " beaten. Backlog slayer earned."
    : beaten + " beaten, " + (BADGE_AT - beaten) + " more lights Backlog slayer.";

  return (
    <div style={{ background: "var(--card)", border: "1px solid var(--line)", borderRadius: 10, padding: "20px clamp(16px,2vw,22px) 22px" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase", color: "var(--mut)" }}>The backlog</div>
          <div style={{ fontSize: "clamp(20px,3vw,26px)", fontWeight: 700, fontFamily: "var(--display)", letterSpacing: "-0.01em", lineHeight: 1.2, marginTop: 10, textWrap: "pretty" }}>{headline}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mut)", marginTop: 7, textWrap: "pretty" }}>{sub}</div>
        </div>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11.5, fontWeight: 700, color: "var(--mut)", background: "var(--card2)", border: "1px solid var(--line)", padding: "8px 13px", borderRadius: 999 }}>
          <i className="fa-solid fa-gamepad" style={{ fontSize: 11, lineHeight: 1, color: "var(--blue, var(--accent))" }} />a tracker, not a to-do list
        </span>
      </div>

      <div style={{ height: 8, borderRadius: 4, background: "var(--raise)", overflow: "hidden", marginTop: 18 }}>
        <span style={{ ...fill(Math.min(100, (beaten / BADGE_AT) * 100)), borderRadius: 4, background: "var(--accent)" }} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginTop: 18 }}>
        {games.map((g) => {
          const tone = TONE[g.status] ?? TONE.backlog!;
          const live = g.status === "playing";
          return (
            <div key={g.id} style={{ display: "flex", flexDirection: "column", gap: 9, padding: 15, borderRadius: 10, background: "var(--card2)", border: `1px solid ${live ? "var(--accent)" : "var(--line)"}` }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: tone.color }}>{tone.label}</span>
                {g.platform && <span style={{ fontSize: 11, fontWeight: 700, color: "var(--mut)" }}>{g.platform}</span>}
              </div>
              <div style={{ fontSize: 13.5, fontWeight: 700, fontFamily: "var(--display)", letterSpacing: "0", lineHeight: 1.3, textWrap: "pretty", color: "var(--ink)" }}>{g.name}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 11.5, fontWeight: 700, color: "var(--mut)", marginTop: "auto" }}>
                {g.status === "beaten" && g.finished && <span>beaten {g.finished}</span>}
                {g.status === "playing" && g.started && <span>since {g.started}</span>}
                {g.hltb_hours > 0 && <span>{g.hltb_hours}h to beat</span>}
                {g.rating > 0 && <span style={{ color: "var(--gold)" }}>{g.rating}/10</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
